import React from 'react';
import { isEmpty, isNil } from 'lodash';

import { MessageReferences } from '~/components/AiChat/components/Message/MessageReferences';
import { ClaimActionPlan } from '~/components/AiChat/SideBarDialog/Insights/ClaimActionPlan';
import { replaceDatesWithLocaleString } from '~/components/AiChat/SideBarDialog/utils';
import type { AiChatReference, Insight, InsightExpertPayload } from '~/components/AiChat/types';
import { ConfidenceSection } from '~/components/ClaimPage/CliveApp/CliveInsights/views/v2/ConfidenceSection';
import { StructuredOutputCard } from '~/components/ClaimPage/CliveApp/CliveInsights/views/v2/StructuredOutputCard';
import CliveMarkdownDisplay from '~/components/Clive/CliveMarkdownDisplay/CliveMarkdownDisplay';

interface InsightCardBodyProps {
  insight: Insight;
  references: Record<string, AiChatReference>;
}

/**
 * The expanded content of one insight version: confidence, the insight text (or the claim action
 * plan), structured output and references.
 *
 * Shared by the latest insight's card and by each history version, so a version in the timeline
 * renders exactly like the card above it. Adding a renderer here reaches both surfaces; adding one
 * to only a caller is how the two would drift.
 */
export const InsightCardBody: React.FC<InsightCardBodyProps> = ({ insight, references }) => {
  const { insight_text, insight_key, related_documents } = insight;
  const payload = insight.payload as InsightExpertPayload | undefined;

  const referencesIds = related_documents ? [...new Set(related_documents.map((doc) => doc?.document_id || ''))] : [];

  const hasConfidence =
    !isNil(payload?.confidence_score) ||
    !isEmpty(payload?.confidence_level) ||
    !isEmpty(payload?.confidence_explanation);

  return (
    <>
      {hasConfidence && payload ? <ConfidenceSection payload={payload} /> : null}
      <div className="flex flex-col gap-8">
        {insight_key === 'claim_action_plan' ? (
          <ClaimActionPlan insight={insight} />
        ) : (
          <CliveMarkdownDisplay
            content={replaceDatesWithLocaleString(insight_text)}
            references={references}
            className="text-base"
          />
        )}
        <StructuredOutputCard structured_output={payload?.structured_output} display_spec={payload?.display_spec} />
        <MessageReferences referencesIds={referencesIds} references={references} />
      </div>
    </>
  );
};
